import React, { useState, useEffect } from 'react';
import {
  adminApiClient,
  ApiSubscriptionPlan,
  ApiSubscriberItem,
  ApiEndpointPermission,
} from '../lib/api';
import { showDeleteConfirmation } from '../lib/sweetAlert';
import {
  Plus,
  Edit2,
  Trash2,
  X,
  Key,
  Users,
  CheckCircle,
  AlertCircle,
  Layers,
  Sparkles,
} from 'lucide-react';
import toast from 'react-hot-toast';

const AVAILABLE_ENDPOINT_CATEGORIES: {
  category: string;
  defaultPath: string;
  defaultMethod: 'GET' | 'POST' | 'ALL';
  description: string;
}[] = [
  {
    category: 'Panchang & Calendar',
    defaultPath: '/api/panchang/*',
    defaultMethod: 'GET',
    description: 'Daily panchang, tithi, nakshatra, and festival calendars',
  },
  {
    category: 'Poojas & Sevas',
    defaultPath: '/api/poojas/*',
    defaultMethod: 'ALL',
    description: 'Pooja listings, details, and booking checkout',
  },
  {
    category: 'Temple Events',
    defaultPath: '/api/temple-events/*',
    defaultMethod: 'ALL',
    description: 'Upcoming festivals, community events, and ticket reservations',
  },
  {
    category: 'Sponsorships (Annadanam)',
    defaultPath: '/api/sponsorships/*',
    defaultMethod: 'ALL',
    description: 'Seva sponsorships, dates calculation, and donor bookings',
  },
  {
    category: 'Donations & Causes',
    defaultPath: '/api/donations/*',
    defaultMethod: 'GET',
    description: 'Donation tracking, causes, and public giving records',
  },
  {
    category: 'Priests Directory',
    defaultPath: '/api/priests/*',
    defaultMethod: 'GET',
    description: 'Temple priest profiles, specializations, and availability',
  },
  {
    category: 'Temple Profiles',
    defaultPath: '/api/temples/*',
    defaultMethod: 'GET',
    description: 'Temple history, darshan timings, deities, and contact info',
  },
  {
    category: 'Full API Suite (All APIs)',
    defaultPath: '/api/*',
    defaultMethod: 'ALL',
    description: 'Unrestricted wildcard access to all platform endpoints',
  },
];

export const ApiSubscriptionPlansPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'plans' | 'subscribers'>('plans');
  const [plans, setPlans] = useState<ApiSubscriptionPlan[]>([]);
  const [subscribers, setSubscribers] = useState<ApiSubscriberItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingPlan, setEditingPlan] = useState<ApiSubscriptionPlan | null>(null);

  // Form State
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    description: '',
    price: 0,
    billingPeriod: 'monthly' as 'monthly' | 'yearly',
    rateLimitPerMinute: 60,
    dailyQuota: 1000,
    monthlyQuota: 30000,
    featuresText: '',
    isActive: true,
    isPopular: false,
    displayOrder: 0,
    allowedEndpoints: [] as ApiEndpointPermission[],
  });

  useEffect(() => {
    fetchData();
  }, [activeTab]);

  const fetchData = async () => {
    try {
      setLoading(true);
      if (activeTab === 'plans') {
        const res = await adminApiClient.getApiSubscriptionPlans();
        if (res.success && res.data) {
          setPlans(res.data.plans || []);
        }
      } else {
        const res = await adminApiClient.getApiSubscribers();
        if (res.success && res.data) {
          setSubscribers(res.data.subscribers || []);
        }
      }
    } catch {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenCreateModal = () => {
    setEditingPlan(null);
    setFormData({
      name: '',
      code: '',
      description: '',
      price: 0,
      billingPeriod: 'monthly',
      rateLimitPerMinute: 60,
      dailyQuota: 1000,
      monthlyQuota: 30000,
      featuresText: 'Daily Quota Included\nStandard Support',
      isActive: true,
      isPopular: false,
      displayOrder: plans.length + 1,
      allowedEndpoints: [
        {
          category: 'Panchang & Calendar',
          pathPattern: '/api/panchang/*',
          method: 'GET',
          description: 'Daily panchang and festival calendars',
        },
      ],
    });
    setShowModal(true);
  };

  const handleOpenEditModal = (plan: ApiSubscriptionPlan) => {
    setEditingPlan(plan);
    setFormData({
      name: plan.name,
      code: plan.code,
      description: plan.description || '',
      price: plan.price || 0,
      billingPeriod: plan.billingPeriod || 'monthly',
      rateLimitPerMinute: plan.limits?.rateLimitPerMinute || 60,
      dailyQuota: plan.limits?.dailyQuota || 1000,
      monthlyQuota: plan.limits?.monthlyQuota || 30000,
      featuresText: (plan.features || []).join('\n'),
      isActive: plan.isActive !== false,
      isPopular: Boolean(plan.isPopular),
      displayOrder: plan.displayOrder || 0,
      allowedEndpoints: plan.allowedEndpoints || [],
    });
    setShowModal(true);
  };

  const toggleEndpointCategory = (cat: typeof AVAILABLE_ENDPOINT_CATEGORIES[0]) => {
    const exists = formData.allowedEndpoints.some((e) => e.pathPattern === cat.defaultPath);
    if (exists) {
      setFormData({
        ...formData,
        allowedEndpoints: formData.allowedEndpoints.filter((e) => e.pathPattern !== cat.defaultPath),
      });
    } else {
      setFormData({
        ...formData,
        allowedEndpoints: [
          ...formData.allowedEndpoints,
          {
            category: cat.category,
            pathPattern: cat.defaultPath,
            method: cat.defaultMethod,
            description: cat.description,
          },
        ],
      });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name.trim() || !formData.code.trim()) {
      toast.error('Plan name and code are required');
      return;
    }

    try {
      const payload: Partial<ApiSubscriptionPlan> = {
        name: formData.name.trim(),
        code: formData.code.trim().toLowerCase(),
        description: formData.description.trim(),
        price: Number(formData.price) || 0,
        billingPeriod: formData.billingPeriod,
        currency: 'USD',
        limits: {
          rateLimitPerMinute: Number(formData.rateLimitPerMinute) || 60,
          dailyQuota: Number(formData.dailyQuota) || 1000,
          monthlyQuota: Number(formData.monthlyQuota) || (Number(formData.dailyQuota) || 1000) * 30,
        },
        features: formData.featuresText
          .split('\n')
          .map((f) => f.trim())
          .filter(Boolean),
        allowedEndpoints: formData.allowedEndpoints,
        isActive: formData.isActive,
        isPopular: formData.isPopular,
        displayOrder: Number(formData.displayOrder) || 0,
      };

      if (editingPlan) {
        const planId = editingPlan._id || editingPlan.id!;
        const res = await adminApiClient.updateApiSubscriptionPlan(planId, payload);
        if (res.success) {
          toast.success('Plan updated successfully');
          setShowModal(false);
          fetchData();
        } else {
          toast.error(res.message || 'Failed to update plan');
        }
      } else {
        const res = await adminApiClient.createApiSubscriptionPlan(payload);
        if (res.success) {
          toast.success('Plan created successfully');
          setShowModal(false);
          fetchData();
        } else {
          toast.error(res.message || 'Failed to create plan');
        }
      }
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Operation failed');
    }
  };

  const handleDelete = async (plan: ApiSubscriptionPlan) => {
    const planId = plan._id || plan.id!;
    const confirmed = await showDeleteConfirmation(
      'Delete or Deactivate Plan?',
      `Are you sure you want to remove ${plan.name}? If it has active subscribers, it will be deactivated instead.`,
      'Yes, proceed'
    );

    if (confirmed) {
      try {
        const res = await adminApiClient.deleteApiSubscriptionPlan(planId);
        if (res.success) {
          toast.success(res.message || 'Plan removed');
          fetchData();
        } else {
          toast.error(res.message || 'Failed to delete plan');
        }
      } catch {
        toast.error('Failed to delete plan');
      }
    }
  };

  const handleSeedDefaults = async () => {
    try {
      const res = await adminApiClient.seedApiSubscriptionPlans();
      if (res.success) {
        toast.success(res.message || 'Default plans initialized');
        fetchData();
      }
    } catch {
      toast.error('Failed to seed default plans');
    }
  };

  const handleToggleSubscriberStatus = async (sub: ApiSubscriberItem) => {
    const nextStatus = sub.status === 'active' ? 'suspended' : 'active';
    try {
      const res = await adminApiClient.updateApiSubscriberStatus(
        sub.id,
        nextStatus,
        nextStatus === 'suspended' ? 'Suspended by Super Admin' : ''
      );
      if (res.success) {
        toast.success(`Subscriber ${nextStatus === 'active' ? 'activated' : 'suspended'}`);
        fetchData();
      }
    } catch {
      toast.error('Failed to update status');
    }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6">
      {/* Page Title & Stats */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pb-4 border-b border-gray-200">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Key className="w-6 h-6 text-orange-500" />
            API Subscriptions & Developer Platform
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Dynamically configure API tiers, request quotas, and allowed endpoints for external developers and partners.
          </p>
        </div>

        {activeTab === 'plans' && (
          <div className="flex items-center gap-2">
            {plans.length === 0 && (
              <button
                onClick={handleSeedDefaults}
                className="px-3.5 py-2 rounded-lg text-sm font-medium border border-orange-200 text-orange-700 bg-orange-50 hover:bg-orange-100 flex items-center gap-1.5 transition-colors"
              >
                <Sparkles className="w-4 h-4" />
                Seed Starter Plans
              </button>
            )}
            <button
              onClick={handleOpenCreateModal}
              className="px-4 py-2 rounded-lg text-sm font-medium bg-orange-600 text-white hover:bg-orange-700 flex items-center gap-2 shadow-sm transition-colors"
            >
              <Plus className="w-4 h-4" />
              Create API Plan
            </button>
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="flex border-b border-gray-200">
        <button
          onClick={() => setActiveTab('plans')}
          className={`pb-3 px-4 text-sm font-medium border-b-2 flex items-center gap-2 transition-colors ${
            activeTab === 'plans'
              ? 'border-orange-500 text-orange-600'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          <Layers className="w-4 h-4" />
          Subscription Plans ({plans.length})
        </button>
        <button
          onClick={() => setActiveTab('subscribers')}
          className={`pb-3 px-4 text-sm font-medium border-b-2 flex items-center gap-2 transition-colors ${
            activeTab === 'subscribers'
              ? 'border-orange-500 text-orange-600'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          <Users className="w-4 h-4" />
          Subscribers & Quotas ({subscribers.length})
        </button>
      </div>

      {/* Plans View */}
      {activeTab === 'plans' && (
        <div className="space-y-4">
          {loading ? (
            <div className="text-center py-12 text-gray-400">Loading API subscription plans...</div>
          ) : plans.length === 0 ? (
            <div className="text-center py-16 bg-gray-50 rounded-xl border border-dashed border-gray-300">
              <Key className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <h3 className="text-base font-semibold text-gray-700">No API Subscription Plans Yet</h3>
              <p className="text-sm text-gray-500 mt-1 max-w-sm mx-auto mb-4">
                Create custom plans to monetize or limit your APIs, or click below to seed recommended tiers.
              </p>
              <button
                onClick={handleSeedDefaults}
                className="px-4 py-2 bg-orange-600 text-white text-sm font-medium rounded-lg hover:bg-orange-700 transition-colors"
              >
                Seed Default Plans (Free, Growth, Enterprise)
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {plans.map((plan) => (
                <div
                  key={plan._id || plan.id}
                  className={`bg-white rounded-xl border p-5 flex flex-col justify-between shadow-sm transition-all hover:shadow-md ${
                    plan.isPopular ? 'border-orange-400 ring-2 ring-orange-100' : 'border-gray-200'
                  }`}
                >
                  <div>
                    <div className="flex items-center justify-between gap-2 mb-2">
                      <span className="font-mono text-xs uppercase px-2 py-0.5 rounded bg-gray-100 text-gray-600 font-semibold">
                        {plan.code}
                      </span>
                      {plan.isPopular && (
                        <span className="text-xs bg-orange-50 text-orange-700 font-medium px-2 py-0.5 rounded-full border border-orange-200">
                          Popular
                        </span>
                      )}
                      {!plan.isActive && (
                        <span className="text-xs bg-gray-100 text-gray-500 font-medium px-2 py-0.5 rounded">
                          Inactive
                        </span>
                      )}
                    </div>

                    <h3 className="text-lg font-bold text-gray-900">{plan.name}</h3>
                    <p className="text-xs text-gray-500 mt-1 line-clamp-2">{plan.description || 'No description'}</p>

                    <div className="mt-4 pb-4 border-b border-gray-100 flex items-baseline gap-1">
                      <span className="text-3xl font-extrabold text-gray-900">
                        {plan.price === 0 ? 'Free' : `$${plan.price}`}
                      </span>
                      {plan.price > 0 && (
                        <span className="text-xs text-gray-400">/{plan.billingPeriod}</span>
                      )}
                    </div>

                    {/* Limits */}
                    <div className="mt-4 space-y-2 text-xs text-gray-600">
                      <div className="flex justify-between">
                        <span className="text-gray-400">Daily Quota:</span>
                        <span className="font-semibold text-gray-800">
                          {plan.limits?.dailyQuota?.toLocaleString() || 0} reqs/day
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-400">Burst Limit:</span>
                        <span className="font-semibold text-gray-800">
                          {plan.limits?.rateLimitPerMinute || 60} reqs/min
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-400">Active Subscribers:</span>
                        <span className="font-semibold text-orange-600">
                          {plan.activeSubscribers || 0}
                        </span>
                      </div>
                    </div>

                    {/* Allowed APIs */}
                    <div className="mt-4 pt-3 border-t border-gray-100">
                      <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1.5">
                        Allowed APIs ({plan.allowedEndpoints?.length || 0})
                      </p>
                      <div className="flex flex-wrap gap-1">
                        {(plan.allowedEndpoints || []).slice(0, 3).map((ep, idx) => (
                          <span
                            key={idx}
                            className="text-[11px] bg-slate-50 border border-slate-200 text-slate-700 px-1.5 py-0.5 rounded"
                          >
                            {ep.category}
                          </span>
                        ))}
                        {(plan.allowedEndpoints?.length || 0) > 3 && (
                          <span className="text-[11px] text-gray-400 self-center">
                            +{plan.allowedEndpoints.length - 3} more
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Card Actions */}
                  <div className="mt-6 pt-3 border-t border-gray-100 flex items-center justify-end gap-2">
                    <button
                      onClick={() => handleOpenEditModal(plan)}
                      className="p-1.5 text-gray-400 hover:text-orange-600 hover:bg-orange-50 rounded transition-colors"
                      title="Edit Plan"
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(plan)}
                      className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                      title="Delete or Deactivate Plan"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Subscribers View */}
      {activeTab === 'subscribers' && (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left font-semibold text-gray-600">Subscriber</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-600">Contact</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-600">Plan</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-600">Today's Usage</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-600">API Keys</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-600">Status</th>
                  <th className="px-4 py-3 text-right font-semibold text-gray-600">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {subscribers.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-4 py-8 text-center text-gray-400">
                      No developers or partners have registered yet.
                    </td>
                  </tr>
                ) : (
                  subscribers.map((sub) => (
                    <tr key={sub.id} className="hover:bg-gray-50/50">
                      <td className="px-4 py-3">
                        <div className="font-medium text-gray-900">{sub.name}</div>
                        {sub.companyName && (
                          <div className="text-xs text-gray-400">{sub.companyName}</div>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-xs text-gray-800">{sub.email}</div>
                        <div className="text-xs text-gray-400">{sub.phone}</div>
                      </td>
                      <td className="px-4 py-3">
                        {sub.currentPlan ? (
                          <span className="inline-block px-2 py-0.5 rounded text-xs font-medium bg-orange-50 text-orange-700 border border-orange-200">
                            {sub.currentPlan.name}
                          </span>
                        ) : (
                          <span className="text-xs text-gray-400">None</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {sub.todayUsage ? (
                          <div className="w-32">
                            <div className="flex justify-between text-[11px] text-gray-500 mb-1">
                              <span>{sub.todayUsage.used} reqs</span>
                              <span>{sub.todayUsage.dailyQuota} max</span>
                            </div>
                            <div className="w-full bg-gray-100 rounded-full h-1.5 overflow-hidden">
                              <div
                                className={`h-full rounded-full ${
                                  sub.todayUsage.percentage > 90
                                    ? 'bg-red-500'
                                    : sub.todayUsage.percentage > 70
                                    ? 'bg-amber-500'
                                    : 'bg-emerald-500'
                                }`}
                                style={{ width: `${Math.min(100, sub.todayUsage.percentage)}%` }}
                              />
                            </div>
                          </div>
                        ) : (
                          <span className="text-xs text-gray-400">0</span>
                        )}
                      </td>
                      <td className="px-4 py-3 font-mono text-xs text-gray-700">
                        {sub.activeKeys} active
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                            sub.status === 'active'
                              ? 'bg-emerald-50 text-emerald-700'
                              : 'bg-red-50 text-red-700'
                          }`}
                        >
                          {sub.status === 'active' ? (
                            <CheckCircle className="w-3 h-3" />
                          ) : (
                            <AlertCircle className="w-3 h-3" />
                          )}
                          {sub.status}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <button
                          onClick={() => handleToggleSubscriberStatus(sub)}
                          className={`text-xs px-2.5 py-1 rounded border transition-colors ${
                            sub.status === 'active'
                              ? 'border-red-200 text-red-600 hover:bg-red-50'
                              : 'border-emerald-200 text-emerald-600 hover:bg-emerald-50'
                          }`}
                        >
                          {sub.status === 'active' ? 'Suspend' : 'Activate'}
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Create / Edit Plan Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4 overflow-y-auto">
          <div className="bg-white rounded-2xl max-w-2xl w-full p-6 shadow-xl my-8">
            <div className="flex items-center justify-between pb-3 border-b border-gray-200 mb-4">
              <h3 className="text-lg font-bold text-gray-900">
                {editingPlan ? 'Edit API Subscription Plan' : 'Create New API Subscription Plan'}
              </h3>
              <button
                onClick={() => setShowModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4 text-sm">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block font-medium text-gray-700 mb-1">Plan Name *</label>
                  <input
                    type="text"
                    required
                    placeholder="e.g. Developer Growth"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-400 outline-none"
                  />
                </div>
                <div>
                  <label className="block font-medium text-gray-700 mb-1">Code Slug *</label>
                  <input
                    type="text"
                    required
                    placeholder="e.g. growth"
                    disabled={Boolean(editingPlan)}
                    value={formData.code}
                    onChange={(e) => setFormData({ ...formData, code: e.target.value.toLowerCase() })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-400 outline-none disabled:bg-gray-100"
                  />
                </div>
              </div>

              <div>
                <label className="block font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  rows={2}
                  placeholder="Target audience and plan summary"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-400 outline-none"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block font-medium text-gray-700 mb-1">Price (USD) *</label>
                  <input
                    type="number"
                    min={0}
                    value={formData.price}
                    onChange={(e) => setFormData({ ...formData, price: Number(e.target.value) })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-400 outline-none"
                  />
                  <p className="text-xs text-gray-400 mt-1">Set to 0 for a free / trial tier</p>
                </div>
                <div>
                  <label className="block font-medium text-gray-700 mb-1">Billing Period</label>
                  <select
                    value={formData.billingPeriod}
                    onChange={(e) =>
                      setFormData({ ...formData, billingPeriod: e.target.value as 'monthly' | 'yearly' })
                    }
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-400 outline-none bg-white"
                  >
                    <option value="monthly">Monthly</option>
                    <option value="yearly">Yearly</option>
                  </select>
                </div>
              </div>

              {/* Quotas & Limits */}
              <div className="p-4 bg-gray-50 rounded-xl space-y-3">
                <h4 className="font-semibold text-gray-800 text-xs uppercase tracking-wider">
                  Request Quotas & Rate Limits
                </h4>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">Rate Limit (per min)</label>
                    <input
                      type="number"
                      min={1}
                      value={formData.rateLimitPerMinute}
                      onChange={(e) =>
                        setFormData({ ...formData, rateLimitPerMinute: Number(e.target.value) })
                      }
                      className="w-full px-3 py-1.5 bg-white border rounded-lg text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">Daily Quota (requests/day)</label>
                    <input
                      type="number"
                      min={1}
                      value={formData.dailyQuota}
                      onChange={(e) =>
                        setFormData({ ...formData, dailyQuota: Number(e.target.value) })
                      }
                      className="w-full px-3 py-1.5 bg-white border rounded-lg text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">Monthly Quota</label>
                    <input
                      type="number"
                      min={1}
                      value={formData.monthlyQuota}
                      onChange={(e) =>
                        setFormData({ ...formData, monthlyQuota: Number(e.target.value) })
                      }
                      className="w-full px-3 py-1.5 bg-white border rounded-lg text-sm"
                    />
                  </div>
                </div>
              </div>

              {/* Endpoint Permissions Matrix */}
              <div>
                <label className="block font-medium text-gray-700 mb-1">
                  Permitted API Endpoints & Categories
                </label>
                <p className="text-xs text-gray-500 mb-2">
                  Select which API categories and routes subscribers on this plan are authorized to call.
                </p>
                <div className="space-y-2 max-h-48 overflow-y-auto border rounded-xl p-3 bg-gray-50/50">
                  {AVAILABLE_ENDPOINT_CATEGORIES.map((cat) => {
                    const isSelected = formData.allowedEndpoints.some(
                      (e) => e.pathPattern === cat.defaultPath
                    );
                    return (
                      <label
                        key={cat.defaultPath}
                        className="flex items-start gap-3 p-2 rounded-lg hover:bg-white cursor-pointer transition-colors"
                      >
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => toggleEndpointCategory(cat)}
                          className="mt-0.5 rounded text-orange-600 focus:ring-orange-400"
                        />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="font-semibold text-gray-800 text-xs">{cat.category}</span>
                            <code className="text-[11px] font-mono text-gray-500 bg-gray-100 px-1 rounded">
                              {cat.defaultPath}
                            </code>
                          </div>
                          <p className="text-xs text-gray-400">{cat.description}</p>
                        </div>
                      </label>
                    );
                  })}
                </div>
              </div>

              {/* Marketing Features */}
              <div>
                <label className="block font-medium text-gray-700 mb-1">
                  Card Features / Bullet Points (One per line)
                </label>
                <textarea
                  rows={3}
                  value={formData.featuresText}
                  onChange={(e) => setFormData({ ...formData, featuresText: e.target.value })}
                  placeholder="5,000 requests / day&#10;Full Panchang API Suite&#10;Priority Email Support"
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-400 outline-none text-xs font-mono"
                />
              </div>

              {/* Toggles */}
              <div className="flex items-center gap-6 pt-2">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.isActive}
                    onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                    className="rounded text-orange-600 focus:ring-orange-400"
                  />
                  <span className="text-xs font-medium text-gray-700">Active / Published</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.isPopular}
                    onChange={(e) => setFormData({ ...formData, isPopular: e.target.checked })}
                    className="rounded text-orange-600 focus:ring-orange-400"
                  />
                  <span className="text-xs font-medium text-gray-700">Badge as "Popular"</span>
                </label>
              </div>

              <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-200">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 border rounded-lg text-gray-600 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-5 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 font-medium"
                >
                  {editingPlan ? 'Save Changes' : 'Create Plan'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ApiSubscriptionPlansPage;

